import React, {createContext, useContext, useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import ngage from "services/ngage";
import {useQuery, UseQueryResult} from "react-query";
import {Operator, OperatorSettings, OperatorSubdomain} from "types/Operator";
import {Navigate} from "react-router-dom";
import BrandedLoader from "components/BrandedLoader";
import {toSnakeCase, toTitleCase} from "services/string";
import {isPlural, singular} from "pluralize";
import convert from 'color-convert';
// Types ----

type OperatorContext = {
  isInitialised: boolean
  subdomain: OperatorSubdomain | null | undefined
  audienceAppUrl: string
  operatorAppUrl: string
  operator: Operator | null | undefined
  settings: OperatorSettings | undefined
  termFor: (term: string) => string
  isMultiSite: boolean
  refetchOperator: UseQueryResult<Operator | null>["refetch"]
}

type OperatorProviderProps = {
  children: React.ReactNode
}

// Helpers ----

function currentSubdomain(): OperatorSubdomain | null {
  // If we're not in production, then the operator can be overridden using an environment variable
  if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_OPERATOR) {
    return process.env.REACT_APP_OPERATOR as OperatorSubdomain
  }

  const appDomain = process.env.REACT_APP_DOMAIN as OperatorSubdomain
  const host = window.location.host

  return (host.replace(appDomain, '')
    .split('.')
    .filter(segment => !!segment)
    .pop()) as OperatorSubdomain ?? null
}

async function initializeOperator(subdomain?: OperatorSubdomain | null): Promise<Operator | null> {
  return subdomain ? await ngage.operators.initialize(subdomain) : null
}

function glossaryKey(term: string): string {
  return toSnakeCase(singular(term).toLowerCase());
}

function normalisedGlossaryTerm(term: string): string {
  return toSnakeCase(term.toLowerCase());
}

function isTitleCase(string: string): boolean {
  return toTitleCase(string) === string;
}

// Context ----

const Context = createContext<OperatorContext>(null!)

// Context Provider ----

export function OperatorProvider({children}: OperatorProviderProps) {
  const subdomain = currentSubdomain();
  const [isInitialised, setIsInitialised] = useState(false);

  const {data: operator, isLoading, refetch: refetchOperator} = useQuery(
    'current-operator',
    () => initializeOperator(subdomain),
    {retry: false},
  )

  useEffect(() => {
    if (!isLoading && !isInitialised) {
      setIsInitialised(true);
    }
  }, [isLoading, isInitialised]);


  // Set the operator brand color in the CSS variables
  useEffect(() => {
    const brandHexColor = operator?.settings.branding.colour_1.replace('#', '');

    if (!brandHexColor) return;
      document.documentElement.style.setProperty(
        "--color-brand",
        convert.hex.hsl(brandHexColor).join(' ')
      );

  }, [operator?.settings.branding.colour_1]);

  const termFor = (term: string): string => {

    const termKey = glossaryKey(term);
    const entry = operator?.settings?.glossary[termKey];

    if (!entry) {
      return term;
    }

    const normalisedTerm = normalisedGlossaryTerm(term);
    const replacement = isPlural(normalisedTerm)? entry.replacement_plural : entry.replacement;

    if(!entry.preserve_case) {
      return isTitleCase(term)? toTitleCase(replacement) : replacement.toLowerCase()
    }

    return replacement;
  }

  const isMultiSite = operator ? operator.usage.sites > 1 : false;

  let contextValue = {
    isInitialised,
    subdomain,
    audienceAppUrl: `app.${subdomain}.${process.env.REACT_APP_DOMAIN}`,
    operatorAppUrl: `${subdomain}.${process.env.REACT_APP_DOMAIN}`,
    operator,
    settings: operator?.settings,
    termFor,
    isMultiSite,
    refetchOperator,
  }

  return (
    <Context.Provider value={contextValue}>
      {!!operator && !!operator.settings.branding.favicon &&
        <Helmet>
          <link rel="shortcut icon" href={operator.settings.branding.favicon.original_url}/>
          <title>N-Gage | {operator.name}</title>
        </Helmet>
      }

      {children}
    </Context.Provider>
  )
}

// Operator Wrapper Component ----

export function RequireOperator({children}: { children: React.ReactElement }) {
  const {operator, subdomain, isInitialised} = useOperator();

  // If there is no subdomain specified, navigate to the n-gage website
  if (!subdomain) {
    window.location.href = 'https://n-gage.io'
    return null
  }

  // Show a loading state while we initialize the operator
  if (!operator && !isInitialised) {
    return <BrandedLoader/>
  }

  // Show an "operator unknown" page if the operator doesn't exist
  if (!operator) {
    return <Navigate to="/unknown" replace/>
  }

  // Show an "operator inactive" page if the operator exists but is not active
  if (!operator.is_active) {
    return <Navigate to="/inactive" replace/>
  }

  // Carry on as normal
  return children
}

// Hook ----

export function useOperator() {
  return useContext(Context)
}